
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';

// Basic length conversion rates (to meters)
const conversionRates = {
  mm: 0.001,
  cm: 0.01,
  m: 1,
  km: 1000,
  in: 0.0254,
  ft: 0.3048,
  yd: 0.9144,
  mi: 1609.344
};

type Unit = keyof typeof conversionRates;

const units = [
  { value: 'mm', label: 'Millimeters (mm)' },
  { value: 'cm', label: 'Centimeters (cm)' },
  { value: 'm', label: 'Meters (m)' },
  { value: 'km', label: 'Kilometers (km)' },
  { value: 'in', label: 'Inches (in)' },
  { value: 'ft', label: 'Feet (ft)' },
  { value: 'yd', label: 'Yards (yd)' },
  { value: 'mi', label: 'Miles (mi)' }
];

const SimpleConverter = () => {
  const [fromValue, setFromValue] = useState('1');
  const [fromUnit, setFromUnit] = useState<Unit>('m');
  const [toUnit, setToUnit] = useState<Unit>('ft');
  const [result, setResult] = useState('');

  useEffect(() => {
    convertUnits();
  }, [fromValue, fromUnit, toUnit]);

  const convertUnits = () => {
    try {
      const value = parseFloat(fromValue);
      if (isNaN(value)) {
        setResult('');
        return;
      }
      
      // Convert to base unit (meters)
      const inBaseUnit = value * conversionRates[fromUnit];
      // Convert from base unit to target unit
      const converted = inBaseUnit / conversionRates[toUnit];
      
      setResult(converted.toFixed(6));
    } catch (error) {
      console.error('Conversion error:', error);
      setResult('Error');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromValue(e.target.value);
  };

  const handleFromUnitChange = (value: string) => {
    setFromUnit(value as Unit);
  };

  const handleToUnitChange = (value: string) => {
    setToUnit(value as Unit);
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
  };

  return (
    <Card className="w-full max-w-sm mx-auto border-4 border-gray-700 rounded-lg shadow-lg bg-gray-800">
      <CardHeader className="p-3 border-b-4 border-gray-700 bg-gray-900">
        <CardTitle className="text-center text-green-500 font-mono">Length Converter</CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4 bg-gray-800">
        <div className="space-y-2">
          <label htmlFor="fromValue" className="block text-sm font-medium text-gray-300">From:</label>
          <div className="flex space-x-2">
            <Input 
              id="fromValue"
              type="number" 
              value={fromValue} 
              onChange={handleInputChange} 
              className="text-right font-mono bg-green-200 text-green-900 border-2 border-gray-700 flex-1" 
              placeholder="Enter value"
            />
            <Select value={fromUnit} onValueChange={handleFromUnitChange}>
              <SelectTrigger className="w-36 bg-gray-600 text-white border-2 border-gray-700">
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 text-white">
                {units.map(unit => (
                  <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-center">
          <Button 
            variant="outline"
            onClick={swapUnits} 
            className="font-mono font-bold border-2 border-gray-800 bg-amber-600 text-white hover:bg-amber-700 px-2 py-1"
          >
            ⇅ Swap
          </Button>
        </div>

        <div className="space-y-2">
          <label htmlFor="toValue" className="block text-sm font-medium text-gray-300">To:</label>
          <div className="flex space-x-2">
            <Input 
              id="toValue"
              type="text" 
              value={result} 
              readOnly 
              className="text-right font-mono bg-green-200 text-green-900 border-2 border-gray-700 flex-1" 
            />
            <Select value={toUnit} onValueChange={handleToUnitChange}>
              <SelectTrigger className="w-36 bg-gray-600 text-white border-2 border-gray-700">
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 text-white">
                {units.map(unit => (
                  <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SimpleConverter;
